import React, { HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import type { SystemSize } from '~/theme';
import Div, { Props as DivProps } from '~/components/atom/Div';

export const GRID_ITEM_ALIGNMENTS = [
  'start',
  'end',
  'center',
  'stretch',
] as const;
export type GridItemAlignment = (typeof GRID_ITEM_ALIGNMENTS)[number];

export const GRID_CONTENT_ALIGNMENTS = [
  'start',
  'end',
  'center',
  'stretch',
  'space-between',
  'space-around',
  'space-evenly',
] as const;
export type GridContentAlignment = (typeof GRID_CONTENT_ALIGNMENTS)[number];

export const GRID_AUTO_FLOWS = [
  'row',
  'column',
  'dense',
  'row dense',
  'column dense',
] as const;
export type GridAutoFlow = (typeof GRID_AUTO_FLOWS)[number];

export type Props = HTMLAttributes<HTMLDivElement> &
  DivProps & {
    dataTestId?: string;
    className?: string;
    children?: React.ReactNode;

    gap?: SystemSize;
    /**
     * Column gap only. Overrides the column part of `gap` if both are provided.
     */
    columnGap?: SystemSize;
    /**
     * Row gap only. Overrides the row part of `gap` if both are provided.
     */
    rowGap?: SystemSize;
    /**
     * Horizontal alignment of items within their grid area.
     */
    justifyItems?: GridItemAlignment;
    /**
     * Vertical alignment of items within their grid area.
     */
    alignItems?: GridItemAlignment;
    /**
     * Horizontal alignment of the whole grid content.
     */
    justifyContent?: GridContentAlignment;
    /**
     * Vertical alignment of the whole grid content.
     */
    alignContent?: GridContentAlignment;
    /**
     * Grid template columns definition.
     * Example: "repeat(3, 1fr)" or "200px 1fr 200px"
     */
    columns?: string;
    /**
     * Grid template rows definition.
     * Example: "auto 1fr auto" or "repeat(2, minmax(100px, auto))"
     */
    rows?: string;
    /**
     * Named grid areas definition.
     * Example:
     * `
     * "header header"
     * "sidebar main"
     * "footer footer"
     * `
     */
    areas?: string;
    /**
     * Auto-placement strategy for implicitly placed items.
     * Example: "row dense", "column", "dense"
     */
    autoFlow?: GridAutoFlow;
    /**
     * Size for implicitly created columns (grid-auto-columns).
     * Example: "minmax(200px, 1fr)" or "200px"
     */
    autoColumns?: string;
    /**
     * Size for implicitly created rows (grid-auto-rows).
     * Example: "minmax(120px, auto)" or "auto"
     */
    autoRows?: string;
  };

const GridContainer = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      dataTestId,
      className,
      children,
      margin,
      padding,
      gap,
      columnGap,
      rowGap,
      justifyItems,
      alignItems,
      justifyContent,
      alignContent,
      columns,
      rows,
      areas,
      autoFlow,
      autoColumns,
      autoRows,
      ...rest
    },
    ref,
  ) => (
    <Container
      dataTestId={dataTestId}
      className={className}
      ref={ref}
      margin={margin}
      padding={padding}
      $gap={gap}
      $columnGap={columnGap}
      $rowGap={rowGap}
      $justifyItems={justifyItems}
      $alignItems={alignItems}
      $justifyContent={justifyContent}
      $alignContent={alignContent}
      $columns={columns}
      $rows={rows}
      $areas={areas}
      $autoFlow={autoFlow}
      $autoColumns={autoColumns}
      $autoRows={autoRows}
      {...rest}
    >
      {children}
    </Container>
  ),
);

type ContainerProps = {
  $gap?: SystemSize;
  $columnGap?: SystemSize;
  $rowGap?: SystemSize;

  $justifyItems?: Props['justifyItems'];
  $alignItems?: Props['alignItems'];
  $justifyContent?: Props['justifyContent'];
  $alignContent?: Props['alignContent'];

  $columns?: Props['columns'];
  $rows?: Props['rows'];
  $areas?: Props['areas'];

  $autoFlow?: Props['autoFlow'];
  $autoColumns?: Props['autoColumns'];
  $autoRows?: Props['autoRows'];
};

const Container = styled(Div)<ContainerProps>(
  ({
    theme,
    $gap,
    $columnGap,
    $rowGap,
    $justifyItems,
    $alignItems,
    $justifyContent,
    $alignContent,
    $columns,
    $rows,
    $areas,
    $autoFlow,
    $autoColumns,
    $autoRows,
  }) => css`
    display: grid;

    ${$columns &&
    css`
      grid-template-columns: ${$columns};
    `}
    ${$rows &&
    css`
      grid-template-rows: ${$rows};
    `}
    ${$areas &&
    css`
      grid-template-areas: ${$areas};
    `}

    ${$autoFlow &&
    css`
      grid-auto-flow: ${$autoFlow};
    `}
    ${$autoColumns &&
    css`
      grid-auto-columns: ${$autoColumns};
    `}
    ${$autoRows &&
    css`
      grid-auto-rows: ${$autoRows};
    `}

    ${$gap &&
    css`
      gap: ${theme.space($gap)};
    `}
    ${$columnGap &&
    css`
      column-gap: ${theme.space($columnGap)};
    `}
    ${$rowGap &&
    css`
      row-gap: ${theme.space($rowGap)};
    `}

    justify-items: ${$justifyItems || 'unset'};
    align-items: ${$alignItems || 'unset'};
    justify-content: ${$justifyContent || 'unset'};
    align-content: ${$alignContent || 'unset'};
  `,
);

export default GridContainer;
